import { HelpCircle } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { HelpSection } from "@/lib/help-content";

// ============================================================================
// Types
// ============================================================================

interface HelpDialogProps {
  /** The help content to display */
  content: HelpSection;
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /**
   * Custom trigger element. If provided, will be wrapped in DialogTrigger.
   * If not provided, no trigger is rendered (control open state externally).
   */
  trigger?: ReactNode;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Reusable help dialog component.
 *
 * Displays a list of help items with terms, descriptions, and optional examples.
 * Can be controlled externally or with an optional trigger element.
 *
 * @example
 * ```tsx
 * // With external control (recommended with useHelpDialog hook)
 * const help = useHelpDialog("priceComparison");
 * <HelpDialog
 *   content={help.content}
 *   open={help.isOpen}
 *   onOpenChange={help.setIsOpen}
 * />
 *
 * // With inline trigger
 * <HelpDialog
 *   content={HELP_CONTENT.priceComparison}
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   trigger={<Button variant="ghost" size="icon"><HelpCircle /></Button>}
 * />
 * ```
 */
export function HelpDialog({
  content,
  open,
  onOpenChange,
  trigger,
}: HelpDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="size-5 text-primary" />
            {content.title}
          </DialogTitle>
          {content.subtitle && (
            <DialogDescription>{content.subtitle}</DialogDescription>
          )}
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] pr-4">
          <dl className="space-y-4">
            {content.items.map((item, index) => (
              <div
                key={item.term}
                className={`pb-4 ${index < content.items.length - 1 ? "border-b" : ""}`}
              >
                <dt className="font-semibold text-foreground">{item.term}</dt>
                <dd className="mt-1 text-sm text-muted-foreground">
                  {item.description}
                </dd>
                {item.example && (
                  <dd className="mt-2 text-xs text-muted-foreground/80 italic bg-muted/50 rounded px-2 py-1">
                    Ej: {item.example}
                  </dd>
                )}
              </div>
            ))}
          </dl>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Convenience Components
// ============================================================================

interface HelpButtonProps {
  /** Click handler to open the help dialog */
  onClick: () => void;
  /** Optional custom label (defaults to just icon) */
  label?: string;
  /** Button variant */
  variant?: "ghost" | "outline" | "default";
  /** Button size */
  size?: "sm" | "default" | "icon";
}

/**
 * Standalone help button that can be placed anywhere.
 * Use with useHelpDialog hook for best results.
 *
 * @example
 * ```tsx
 * const help = useHelpDialog("priceComparison");
 * <HelpButton onClick={help.open} />
 * ```
 */
export function HelpButton({
  onClick,
  label,
  variant = "ghost",
  size = "sm",
}: HelpButtonProps) {
  return (
    <Button
      variant={variant}
      size={size}
      onClick={onClick}
      className="gap-1.5"
      aria-label="Abrir ayuda"
    >
      <HelpCircle className="size-4" />
      {label && <span className="hidden sm:inline">{label}</span>}
    </Button>
  );
}
